import { motion } from "framer-motion";
import { ArrowRight, Bell, Calendar, Clock, Code2, Lightbulb, Mail, MessageCircle, Music, Puzzle, Users } from "lucide-react";
import ContactForm from "../components/ContactForm";
import ImageCarousel from "../components/ImageCarousel";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

const blue = {
  accent: "#2B87BE",
  brand: "#1B5F7E",
  light: "#EBF5FB",
  mist: "#F5FBFE",
  border: "#CDE5F1",
  deep: "#0D3A4F",
};

const campamentoItems = [
  {
    title: "Creatividad",
    desc: "Proyectos, retos abiertos y propuestas expresivas para imaginar, construir y transformar ideas.",
    icon: Lightbulb,
  },
  {
    title: "Pensamiento lógico",
    desc: "Juegos, problemas, estrategias y desafíos que invitan a razonar, probar caminos y disfrutar del proceso.",
    icon: Puzzle,
  },
  {
    title: "Informática",
    desc: "Actividades digitales y tecnológicas adaptadas al grupo, con foco en la creación y el pensamiento computacional.",
    icon: Code2,
  },
  {
    title: "Música",
    desc: "Exploración sonora, ritmo, escucha y creación musical como forma de expresión y encuentro.",
    icon: Music,
  },
  {
    title: "Calma, juego y conversación",
    desc: "Tiempos para descansar, hablar, jugar, compartir intereses y sentirse parte del grupo.",
    icon: MessageCircle,
  },
];

const infoRows = [
  ["Edición", "Verano 2027, en preparación.", Calendar],
  ["Fechas", "Sin fechas cerradas todavía.", Calendar],
  ["Horario orientativo", "Mañanas, con horario final por confirmar.", Clock],
  ["Lugar", "San Sebastián de los Reyes, ubicación final por confirmar.", Users],
  ["Edad recomendada", "6 a 12 años.", Users],
  ["Aviso", "Te escribiremos cuando publiquemos la información.", Bell],
];

const urbanPhotos = [
  {
    url: "https://images.unsplash.com/photo-1759646827278-27c5733e0cee?auto=format&fit=crop&w=1600&q=80",
    caption: "Informática y pensamiento digital",
  },
  {
    url: "https://images.unsplash.com/photo-1758685734153-132c8620c1bd?auto=format&fit=crop&w=1600&q=80",
    caption: "Exploración y curiosidad",
  },
  {
    url: "https://images.unsplash.com/photo-1770652453111-c38e37f5e6ad?auto=format&fit=crop&w=1600&q=80",
    caption: "Juego de mesa y estrategia",
  },
  {
    url: "https://images.unsplash.com/photo-1763635031729-b3db264dd8c0?auto=format&fit=crop&w=1600&q=80",
    caption: "Pensamiento lógico",
  },
  {
    url: "https://images.unsplash.com/photo-1711048421235-3fcb9dcf82f7?auto=format&fit=crop&w=1600&q=80",
    caption: "Música y ritmo",
  },
  {
    url: "https://images.unsplash.com/photo-1725596854669-61fb4badaf4d?auto=format&fit=crop&w=1600&q=80",
    caption: "Materiales de aprendizaje",
  },
];

export default function CampamentoUrbano() {
  return (
    <main data-testid="campamento-urbano-page" style={{ fontFamily: "Manrope, sans-serif" }}>
      <section className="relative overflow-hidden pt-32 pb-20 px-5 lg:px-8" style={{ background: blue.deep }}>
        <div className="absolute inset-0 opacity-25">
          <img
            src="/images/campamento/grupo-jovenes.jpg"
            alt="Grupo de jóvenes en una actividad de campamento"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(13,58,79,0.96) 0%, rgba(13,58,79,0.82) 62%, rgba(13,58,79,0.64) 100%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.7 }}
            variants={fadeUp}
            className="max-w-3xl"
          >
            <div className="section-label mb-6" style={{ color: "#B8D9E8" }}>
              Campamento Urbano · Verano 2027
            </div>
            <h1
              style={{ fontFamily: "Playfair Display, serif", lineHeight: 1.08 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-7"
            >
              Un verano para seguir pensando, creando y{" "}
              <em style={{ color: "#B8D9E8" }}>encontrándose.</em>
            </h1>
            <p className="text-base md:text-lg text-white/75 leading-relaxed max-w-2xl mb-9">
              Estamos preparando el Campamento Urbano DAC-PAC 2027. Todavía no hay fechas cerradas; cuando publiquemos calendario, horarios, ubicación, precios y condiciones, avisaremos a las familias interesadas.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#aviso-urbano" className="btn-primary">
                Quiero que me aviséis <ArrowRight size={16} />
              </a>
              <a href="#informacion-practica" className="btn-outline bg-white/5 border-white/70 text-white hover:bg-white hover:text-[#0D3A4F]">
                Ver orientación
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.section
        className="py-24 px-5 lg:px-8"
        style={{ background: "#FFFFFF" }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        variants={fadeUp}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
          <div>
            <div className="section-label mb-4" style={{ color: blue.accent }}>Campamento Urbano 2027</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: blue.deep }} className="text-4xl md:text-5xl font-semibold leading-tight">
              Un espacio flexible para vivir el verano <em style={{ color: blue.brand }}>con sentido.</em>
            </h2>
          </div>
          <div className="space-y-5 text-slate-600 leading-relaxed">
            <p>
              El verano también puede ser un tiempo para seguir pensando, creando y encontrándose con otras formas de mirar el mundo. En el Campamento Urbano DAC-PAC proponemos un espacio cuidado, flexible y estimulante, donde cada participante pueda explorar ideas, proyectos y lenguajes diversos sin perder de vista el juego, la calma y la convivencia.
            </p>
            <p>
              La edición de 2027 está en preparación. Mantendremos esta página como punto informativo y enviaremos la información cuando el programa esté cerrado.
            </p>
            <p>
              Está pensado para niños, niñas y jóvenes con altas capacidades o perfiles de alta curiosidad, sensibilidad e intensidad, en un entorno respetuoso, cercano y atento a las necesidades reales de cada participante.
            </p>
          </div>
        </div>
      </motion.section>

      <section className="py-24 px-5 lg:px-8" style={{ background: blue.mist }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="max-w-2xl mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
          >
            <div className="section-label mb-4" style={{ color: blue.accent }}>Propuesta</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: blue.deep }} className="text-4xl md:text-5xl font-semibold mb-5">
              ¿Qué encontrarán en el campamento?
            </h2>
            <p className="text-slate-600 leading-relaxed">
              El programa definitivo de 2027 se publicará más adelante, pero la línea de trabajo seguirá combinando pensamiento, creación, juego, tecnología, música y tiempos de encuentro.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campamentoItems.map((item, index) => (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.06 }}
                variants={fadeUp}
                className="rounded-2xl bg-white p-7"
                style={{ border: `1px solid ${blue.border}` }}
              >
                <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-5" style={{ background: blue.light }}>
                  <item.icon size={20} style={{ color: blue.accent }} />
                </div>
                <h3 style={{ fontFamily: "Playfair Display, serif", color: blue.deep }} className="text-xl font-semibold mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 items-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            variants={fadeUp}
          >
            <div>
              <div className="section-label mb-4" style={{ color: blue.accent }}>Propuestas del campamento</div>
              <h2 style={{ fontFamily: "Playfair Display, serif", color: blue.deep }} className="text-4xl md:text-5xl font-semibold mb-5">
                Un ambiente de trabajo, juego y creación
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Una aproximación visual al tipo de propuestas que combinamos: pensamiento, creación, juego, tecnología, música y tiempos de encuentro.
              </p>
            </div>

            <div className="rounded-3xl overflow-hidden" style={{ border: `1px solid ${blue.border}`, background: blue.mist }}>
              <ImageCarousel images={urbanPhotos} height="420px" />
            </div>
          </motion.div>
        </div>
      </section>

      <motion.section
        className="py-24 px-5 lg:px-8"
        style={{ background: blue.light }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        variants={fadeUp}
      >
        <div className="max-w-5xl mx-auto">
          <div className="section-label mb-4" style={{ color: blue.accent }}>Acompañamiento</div>
          <h2 style={{ fontFamily: "Playfair Display, serif", color: blue.deep }} className="text-4xl md:text-5xl font-semibold mb-7">
            Pensado para perfiles con altas capacidades
          </h2>
          <div className="space-y-5 text-slate-700 leading-relaxed">
            <p>
              Algunas niñas, niños y jóvenes viven el aprendizaje con una curiosidad muy intensa, hacen conexiones rápidas, necesitan profundidad, preguntan mucho o se frustran cuando todo resulta demasiado repetitivo.
            </p>
            <p>
              También pueden necesitar más tiempo para regularse, espacios donde sentirse comprendidos, propuestas con reto real y adultos que acompañen sin etiquetar ni simplificar lo que les ocurre.
            </p>
            <p>
              Este campamento está diseñado teniendo en cuenta esa combinación de curiosidad, sensibilidad, intensidad y necesidad de vínculo. No se trata de exigir más, sino de ofrecer un entorno donde puedan participar con autenticidad, encontrar intereses compartidos y disfrutar del verano.
            </p>
          </div>
        </div>
      </motion.section>

      <section id="informacion-practica" className="py-24 px-5 lg:px-8 scroll-mt-24" style={{ background: blue.mist }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
          >
            <div className="section-label mb-4" style={{ color: blue.accent }}>Información práctica</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: blue.deep }} className="text-4xl md:text-5xl font-semibold">
              Edición 2027 en preparación
            </h2>
            <p className="text-slate-600 leading-relaxed mt-5">
              Todavía no hay calendario ni condiciones definitivas. Esta es la orientación inicial mientras cerramos la próxima edición.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {infoRows.map(([label, value, Icon], index) => (
              <motion.div
                key={label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.04 }}
                variants={fadeUp}
                className="flex gap-5 rounded-2xl bg-white p-6"
                style={{ border: `1px solid ${blue.border}` }}
              >
                <div className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: blue.light }}>
                  <Icon size={19} style={{ color: blue.accent }} />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: blue.accent }}>{label}</div>
                  <div className="text-sm font-semibold leading-relaxed" style={{ color: blue.deep }}>{value}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="aviso-urbano" className="py-24 px-5 lg:px-8 scroll-mt-24" style={{ background: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
          >
            <div className="section-label mb-4" style={{ color: blue.accent }}>Aviso de publicación</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: blue.deep }} className="text-4xl md:text-5xl font-semibold mb-6">
              Te escribimos cuando esté listo
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Déjanos tus datos y te enviaremos la información del Campamento Urbano 2027 cuando se publiquen fechas, horarios, precios, ubicación y proceso de reserva.
              </p>
              <p>
                No es una reserva de plaza. Es una lista de interés para recibir la información en cuanto esté disponible.
              </p>
            </div>
            <div className="mt-8 rounded-2xl p-5 border border-[#CDE5F1]" style={{ background: blue.mist }}>
              <Mail size={20} style={{ color: blue.accent }} className="mb-3" />
              <h3 className="font-semibold text-sm mb-2" style={{ color: blue.deep }}>Información sin compromiso</h3>
              <p className="text-xs text-slate-500 leading-relaxed">Usaremos este contacto solo para responderte y enviarte la información del campamento urbano cuando esté publicada.</p>
            </div>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            variants={fadeUp}
            className="rounded-2xl p-6 md:p-8 border border-[#CDE5F1]"
            style={{ background: blue.mist }}
          >
            <ContactForm
              accentColor={blue.accent}
              origen="aviso-campamento-urbano-2027"
              initialMensaje="Quiero recibir la información del Campamento Urbano DAC-PAC 2027 cuando se publique."
              submitLabel="Solicitar aviso"
              successTitle="Solicitud recibida"
              successText="Te escribiremos cuando publiquemos la información del Campamento Urbano 2027."
              testPrefix="campamento-urbano-aviso"
            />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
